import { Subject } from '@mui/icons-material';
import {
  ClickAwayListener,
  Divider,
  Grow,
  ListItemIcon,
  ListItemText,
  MenuItem,
  MenuList,
  Paper,
  Popper,
} from '@mui/material';
import Typography from '@mui/material/Typography';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import LogoutIcon from '@mui/icons-material/Logout';
import SettingsIcon from '@mui/icons-material/Settings';
import { AppMode } from '@/modules/common/types/general';
import { DebugMenu } from '@/modules/debug/components/DebugMenu';
import { showDebugMenu } from '@/modules/debug/helpers';
import { useUserInfo } from '@modules/authentication/hooks/useUserInfo';
import { AppServiceFactory } from '@/modules/app/service/AppServiceFactory';

import VersionList from './VersionList';

const buttonStyle = {
  width: '2rem',
  height: '2rem',
  borderRadius: '9999px',
  overflow: 'hidden',
  padding: 0,
  cursor: 'pointer',
} as const;

type Props = {
  appMode: AppMode;
};

export function UserMenu({ appMode }: Props) {
  const { accountInfo, getName, getAvatarImage, signOut } = useUserInfo();
  const [avatar, setAvatar] = useState<string | undefined>('');
  const [name, setName] = useState('');
  const [initials, setInitials] = useState('');
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const anchorRef = useRef(null);
  const { t } = useTranslation(['common', 'interface']);
  const appSvc = useMemo(() => AppServiceFactory.getInstance().createAppService(), []);

  useEffect(() => {
    if (!accountInfo) return;

    const fullName = getName();
    if (fullName) {
      // plz fix me
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setName(fullName);
      setInitials(
        fullName
          .split(' ')
          .map((e) => e.substring(0, 1))
          .join(''),
      );
    }
    getAvatarImage().then((imageUrl) => {
      setAvatar(imageUrl);
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountInfo]);

  const handleProfileDropDownClose = useCallback((event: Event) => {
    // @ts-expect-error strictNullChecks. Pls fix me
    if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
      return;
    }

    setProfileDropdownOpen(false);
  }, []);

  const handleSignOut = useCallback(() => {
    setProfileDropdownOpen(false);
    signOut();
  }, [signOut]);

  const handleOpenSettings = useCallback(() => {
    setProfileDropdownOpen(false);
    appSvc.setActiveModalId('settings');
  }, [appSvc]);

  const handleOpenReleaseNotes = useCallback(() => {
    setProfileDropdownOpen(false);
    appSvc.setActiveModalId('release_notes');
  }, [appSvc]);

  return (
    <>
      {accountInfo && (
        <button
          ref={anchorRef}
          data-testid='profile-menu-button'
          onClick={() => {
            setProfileDropdownOpen(!profileDropdownOpen);
          }}
          style={buttonStyle}
        >
          {avatar && (
            <img
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              src={avatar}
              alt={name}
            />
          )}
          {!avatar && (
            <Typography
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                borderWidth: '1px',
                borderColor: 'neutral.main',
                '&:hover': { cursor: 'pointer' },
              }}
            >
              {initials}
            </Typography>
          )}
        </button>
      )}
      <Popper
        sx={{
          zIndex: 1,
        }}
        open={profileDropdownOpen}
        // plz fix me
        // eslint-disable-next-line react-hooks/refs
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        placement='bottom-end'
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin: placement === 'bottom-end' ? 'right top' : 'center bottom',
            }}
          >
            <Paper>
              <ClickAwayListener onClickAway={handleProfileDropDownClose}>
                <MenuList id='split-button-menu' autoFocusItem sx={{ maxWidth: 250 }}>
                  <MenuItem disabled>{getName()}</MenuItem>
                  <Divider />
                  {appMode === AppMode.DigitalTwin && [
                    <MenuItem onClick={handleOpenSettings} key='settings'>
                      <ListItemIcon>
                        <SettingsIcon fontSize='small' />
                      </ListItemIcon>
                      <ListItemText>{t('common:settings', 'settings')}</ListItemText>
                    </MenuItem>,
                  ]}
                  {showDebugMenu && <DebugMenu />}
                  <MenuItem onClick={handleOpenReleaseNotes} data-testid='releaseNotes-link'>
                    <ListItemIcon>
                      <Subject fontSize='small' />
                    </ListItemIcon>
                    <ListItemText>{t('common:release_notes', 'Release Notes')}</ListItemText>
                  </MenuItem>
                  <MenuItem onClick={handleSignOut} data-testid='signout-link'>
                    <ListItemIcon>
                      <LogoutIcon fontSize='small' />
                    </ListItemIcon>
                    <ListItemText>{t('common:logout', 'Log out')}</ListItemText>
                  </MenuItem>
                  <Divider />
                  <MenuItem disabled>
                    <VersionList />
                  </MenuItem>
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  );
}
